import { FERTILITY_COST_CATALOG } from "./catalog";
import type {
  CostAvailabilityPolicy,
  CostCatalogEntry,
  EstimateLineId,
  FertilityCostCatalog,
} from "./catalog";
import type { TreatmentType } from "@src/packages/homepage/utils/costEstimator";

export type EstimateLocationInput = {
  countryCode: string;
  stateCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  clinicIds?: string[];
};

export type ResolvedEstimatePrice = {
  amount: number;
  lineId: EstimateLineId;
  unit: CostCatalogEntry["unit"];
  currency: CostCatalogEntry["currency"];
  catalogVersion: string;
  resolution: "clinic" | "nearby-clinics" | "city" | "state" | "country";
  entries: CostCatalogEntry[];
  confidence: CostCatalogEntry["confidence"];
};

type ResolvePriceParams = {
  lineId: EstimateLineId;
  location: EstimateLocationInput;
  catalog?: FertilityCostCatalog;
};

function normalize(value?: string) {
  return value?.trim().toLowerCase();
}

function confidenceRank(confidence: CostCatalogEntry["confidence"]) {
  if (confidence === "high") return 3;
  if (confidence === "medium") return 2;
  return 1;
}

function lowestConfidence(entries: CostCatalogEntry[]) {
  return entries.reduce<CostCatalogEntry["confidence"]>(
    (lowest, entry) =>
      confidenceRank(entry.confidence) < confidenceRank(lowest)
        ? entry.confidence
        : lowest,
    "high",
  );
}

function averageEntries(
  entries: CostCatalogEntry[],
  resolution: ResolvedEstimatePrice["resolution"],
  catalogVersion: string,
): ResolvedEstimatePrice | null {
  if (entries.length === 0) return null;
  const amount = Math.round(
    entries.reduce((sum, entry) => sum + entry.amount, 0) / entries.length,
  );
  const firstEntry = entries[0]!;

  return {
    amount,
    lineId: firstEntry.lineId,
    unit: firstEntry.unit,
    currency: firstEntry.currency,
    catalogVersion,
    resolution,
    entries,
    confidence: lowestConfidence(entries),
  };
}

function distanceMiles(
  from: Required<Pick<EstimateLocationInput, "latitude" | "longitude">>,
  to: Required<Pick<CostCatalogEntry["location"], "latitude" | "longitude">>,
) {
  const earthRadiusMiles = 3958.8;
  const fromLat = (from.latitude * Math.PI) / 180;
  const toLat = (to.latitude * Math.PI) / 180;
  const deltaLat = ((to.latitude - from.latitude) * Math.PI) / 180;
  const deltaLon = ((to.longitude - from.longitude) * Math.PI) / 180;
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(fromLat) *
      Math.cos(toLat) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Resolves a price by cascading clinic → nearby clinics → city → state →
 * country. Returns `null` when no catalog row covers the location so that
 * callers can degrade (hide the line, show a region placeholder) instead of
 * crashing. The catalog ships US-only pricing today; non-US locations will
 * resolve to `null` for every line until regional rows are ingested.
 */
export function resolveEstimatePrice({
  lineId,
  location,
  catalog = FERTILITY_COST_CATALOG,
}: ResolvePriceParams): ResolvedEstimatePrice | null {
  const candidates = catalog.entries.filter(
    (entry) =>
      entry.lineId === lineId &&
      entry.location.countryCode === location.countryCode &&
      // "available" rows have market prices. "funded" / "partially-funded"
      // rows carry a patient-paid amount (often 0 when the public system
      // covers it) and should resolve. "not-available" and "restricted"
      // rows are policy-only — UI reads them via getLineAvailability.
      (entry.availability.status === "available" ||
        entry.availability.status === "funded" ||
        entry.availability.status === "partially-funded"),
  );
  const selectedClinicEntries =
    location.clinicIds && location.clinicIds.length > 0
      ? candidates.filter(
          (entry) =>
            entry.location.scope === "clinic" &&
            entry.location.clinicId != null &&
            location.clinicIds!.includes(entry.location.clinicId),
        )
      : [];
  const selectedClinicAverage = averageEntries(
    selectedClinicEntries,
    "clinic",
    catalog.version,
  );

  if (selectedClinicAverage) return selectedClinicAverage;

  if (location.latitude != null && location.longitude != null) {
    const nearbyClinicEntries = candidates
      .filter(
        (entry) =>
          entry.location.scope === "clinic" &&
          entry.location.latitude != null &&
          entry.location.longitude != null,
      )
      .map((entry) => ({
        entry,
        distance: distanceMiles(
          {
            latitude: location.latitude!,
            longitude: location.longitude!,
          },
          {
            latitude: entry.location.latitude!,
            longitude: entry.location.longitude!,
          },
        ),
      }))
      .filter(({ distance }) => distance <= 250)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5)
      .map(({ entry }) => entry);
    const nearbyAverage = averageEntries(
      nearbyClinicEntries,
      "nearby-clinics",
      catalog.version,
    );

    if (nearbyAverage) return nearbyAverage;
  }

  const cityEntries = candidates.filter(
    (entry) =>
      entry.location.scope === "city" &&
      normalize(entry.location.city) === normalize(location.city) &&
      entry.location.stateCode === location.stateCode,
  );
  const cityAverage = averageEntries(cityEntries, "city", catalog.version);

  if (cityAverage) return cityAverage;

  const stateEntries = candidates.filter(
    (entry) =>
      entry.location.scope === "state" &&
      entry.location.stateCode === location.stateCode,
  );
  const stateAverage = averageEntries(stateEntries, "state", catalog.version);

  if (stateAverage) return stateAverage;

  const countryEntries = candidates.filter(
    (entry) => entry.location.scope === "country",
  );
  const countryAverage = averageEntries(
    countryEntries,
    "country",
    catalog.version,
  );

  if (countryAverage) return countryAverage;

  return null;
}

export function resolveEstimateAmount(
  lineId: EstimateLineId,
  location: EstimateLocationInput,
): number | null {
  return resolveEstimatePrice({ lineId, location })?.amount ?? null;
}

/**
 * Whether the catalog can price this location below the country level (a
 * clinic/city/state row actually matches it). Mirrors the cascade match in
 * `resolveEstimatePrice`. Used to decide location-specific copy: with a
 * country-only catalog this is always false, so the estimate is honestly
 * labeled a national average instead of claiming "in this location". Becomes
 * true automatically once matching sub-country price rows are ingested.
 */
export function isLocationSpecificEstimate(
  location: EstimateLocationInput,
  catalog: FertilityCostCatalog = FERTILITY_COST_CATALOG,
): boolean {
  return catalog.entries.some((entry) => {
    if (entry.location.countryCode !== location.countryCode) return false;
    switch (entry.location.scope) {
      case "clinic":
        return (
          entry.location.clinicId != null &&
          (location.clinicIds ?? []).includes(entry.location.clinicId)
        );
      case "city":
        return (
          normalize(entry.location.city) === normalize(location.city) &&
          entry.location.stateCode === location.stateCode
        );
      case "state":
        return entry.location.stateCode === location.stateCode;
      default:
        return false;
    }
  });
}

/**
 * Looks up the country-level availability policy for a line in a given
 * location — anything other than a plain "available" market price. Used by
 * UI to render both the "Available in your region" (funded / partially-
 * funded) section with coverage notes/waits/limits AND the "Not available
 * in your region" (restricted / not-available) section with cross-border
 * destinations. Funded lines still appear in the main breakdown at €0 —
 * this surface complements it by naming the funding source and limits.
 */
export function getLineAvailability(
  lineId: EstimateLineId,
  location: EstimateLocationInput,
  catalog: FertilityCostCatalog = FERTILITY_COST_CATALOG,
): CostAvailabilityPolicy | null {
  const entry = catalog.entries.find(
    (candidate) =>
      candidate.lineId === lineId &&
      candidate.location.countryCode === location.countryCode &&
      candidate.location.scope === "country" &&
      candidate.availability.status !== "available",
  );
  return entry?.availability ?? null;
}

export function getMedicationLineId(
  treatment: TreatmentType,
): EstimateLineId | null {
  if (treatment === "sperm_freezing") return null;
  if (treatment === "fet") return "medications-fet";
  if (treatment === "iui") return "medications-iui";
  if (treatment === "ovulation_induction") return "medications-oi";
  return "medications-stimulation";
}

export function hasMedicationEstimate(treatment: TreatmentType) {
  return getMedicationLineId(treatment) != null;
}

export function roundModeledEstimate(amount: number) {
  const absAmount = Math.abs(amount);
  // Modeled averages are rounded into readable bins; the bin size scales with
  // the magnitude so a $11,200 cycle rounds to $250s but a €41 storage fee
  // rounds to €5s instead of jumping to €50 (which lost 20% of the value).
  const increment =
    absAmount >= 10000
      ? 250
      : absAmount >= 1000
        ? 100
        : absAmount >= 100
          ? 50
          : 5;
  return Math.round(amount / increment) * increment;
}
